import { defineStore } from 'pinia';
import { confirmJackpotSync, getJackpotSyncStatus } from '@/api/jackpot';
import { refreshJackpotFeature } from '@/composables/useJackpotFeature.js';
import { getAccessToken } from '@/utils/auth-token';
import { isUserRejectedError, sendRcTokenTransfer } from '@/utils/rc-deposit-transfer';
import { getDusdContractAddress } from '@/utils/trade-contracts';

function emptyState() {
  return {
    pending: false,
    /** @type {'silent' | 'manual'} */
    alignMode: 'silent',
    totalBurn: '0',
    totalMint: '0',
    treasury: '',
    /** @type {Array<{ id?: string|number, roundId?: string|number, debtType?: string, amount?: string|number, status?: string }>} */
    debts: [],
    loading: false,
    aligning: false,
    lastError: '',
  };
}

export const useJackpotSyncStore = defineStore('jackpotSync', {
  state: () => ({
    ...emptyState(),
    /** 递增以丢弃过期的 refresh 写回 */
    _refreshGen: 0,
  }),

  getters: {
    /** 未对齐时禁止市场交易 */
    blocksMarketTrade: (state) => state.pending === true,
    /** 未对齐时禁止提现 */
    blocksWithdraw: (state) => state.pending === true,
    needsManualAlign(state) {
      return state.pending && state.alignMode === 'manual' && Number(state.totalBurn) > 0;
    },
    isSilentPending(state) {
      return state.pending && state.alignMode === 'silent';
    },
    isMintWaitPending(state) {
      return (
        state.pending && state.alignMode === 'manual' && !(Number(state.totalBurn) > 0)
      );
    },
  },

  actions: {
    reset() {
      this._refreshGen += 1;
      Object.assign(this, emptyState());
    },

    async refresh() {
      const gen = ++this._refreshGen;
      const on = await refreshJackpotFeature();
      if (gen !== this._refreshGen) return;
      if (!on || !getAccessToken()) {
        Object.assign(this, emptyState());
        return;
      }
      this.loading = true;
      try {
        const data = await getJackpotSyncStatus();
        if (gen !== this._refreshGen) return;
        this.pending = !!data?.pending;
        const mode =
          data?.alignMode || (data?.silentAlign === false ? 'manual' : 'silent');
        this.alignMode = mode === 'manual' ? 'manual' : 'silent';
        this.totalBurn = data?.totalBurn || '0';
        this.totalMint = data?.totalMint || '0';
        this.treasury = data?.treasury || '';
        this.debts = Array.isArray(data?.debts) ? data.debts : [];
        this.lastError = '';
      } catch {
        if (gen !== this._refreshGen) return;
        // 拉取失败 fail-open：不挡交易；有轮询会重试
        this.pending = false;
        this.alignMode = 'silent';
        this.totalBurn = '0';
        this.totalMint = '0';
        this.treasury = '';
        this.debts = [];
      } finally {
        if (gen === this._refreshGen) {
          this.loading = false;
        }
      }
    },

    /**
     * EOA 手动 BURN：转 pUSD 到 treasury 后上报 confirm。
     * @returns {Promise<{ ok: true, txHash: string } | { ok: false, rejected?: boolean, message: string }>}
     */
    async confirmManualAlign() {
      if (this.aligning) {
        return { ok: false, message: 'busy' };
      }
      this.lastError = '';
      this.aligning = true;
      try {
        const to = String(this.treasury || '').trim();
        const token = getDusdContractAddress();
        if (!to) throw new Error('treasury missing');
        if (!token) throw new Error('VUE_APP_DUSD_CONTRACT not configured');
        const amount = String(this.totalBurn || '0');
        if (!(Number(amount) > 0)) throw new Error('nothing to burn');

        const { txHash: hash } = await sendRcTokenTransfer({
          toAddress: to,
          contractAddress: token,
          amount,
          decimals: 18,
          chainId: Number(process.env.VUE_APP_CHAIN_ID) || 137,
          waitForConfirmation: true,
        });
        const ids = this.debts
          .filter((d) => d?.debtType === 'BURN')
          .map((d) => d.id)
          .filter((id) => id != null);
        await confirmJackpotSync({ debtIds: ids, txHash: hash });
        await this.refresh();
        return { ok: true, txHash: hash };
      } catch (e) {
        if (isUserRejectedError?.(e)) {
          this.lastError = 'rejected';
          return { ok: false, rejected: true, message: 'rejected' };
        }
        const message = e?.message || String(e);
        this.lastError = message;
        return { ok: false, message };
      } finally {
        this.aligning = false;
      }
    },
  },
});

/** 登出清理：停轮询并清空状态 */
export function clearJackpotSyncStore() {
  try {
    useJackpotSyncStore().reset();
  } catch {
    /* Pinia 未就绪 */
  }
}
